import { useEffect, useMemo } from "react";

import { KpkApiContactSheet } from "@keepeek/api-client";
import {
  contactSheetsSelector,
  currentLocaleAtom,
  maxElementsByContactSheetSelector,
} from "@keepeek/commons";
import { KpkContactSheet } from "@keepeek/refront-components";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";

import {
  ContactSheetsModalOpenStatus,
  contactSheetsModalOpenStatusState,
} from "../atoms/selections";

export type UseContactSheets = {
  contactSheets: KpkContactSheet[] | undefined;
  loadingContactSheets: boolean;
};

export const useContactSheets = (): UseContactSheets => {
  const contactSheetQuery = useRecoilValueLoadable(contactSheetsSelector);
  const currentLocale = useRecoilValue(currentLocaleAtom);
  const contactSheets = useMemo(() => {
    if (!currentLocale) {
      console.debug("useContactSheets current locale atom is not valued");
      return [];
    }
    return convertToKpkContactSheet(
      currentLocale,
      contactSheetQuery.state === "hasValue" ? contactSheetQuery.contents : undefined,
    );
  }, [contactSheetQuery.contents, contactSheetQuery.state, currentLocale]);
  const loadingContactSheets = contactSheetQuery.state === "loading";
  const snackBar = useSnackbar();
  const { t } = useTranslation();

  // Manage error
  const [status, setStatus] = useRecoilState(contactSheetsModalOpenStatusState);
  const maxElementsQuery = useRecoilValueLoadable(maxElementsByContactSheetSelector);
  const maxElements = maxElementsQuery.state === "hasValue" ? maxElementsQuery.contents : 0;

  useEffect(() => {
    switch (status) {
      case ContactSheetsModalOpenStatus.MAX_REACHED:
        snackBar.enqueueSnackbar(
          t("contact-sheet.status.max-reached.label", { count: maxElements }),
          { variant: "warning" },
        );
        break;
      default:
        break;
    }

    return () => {
      // Reset after print
      setStatus(ContactSheetsModalOpenStatus.ALLOWED);
    };
  }, [maxElements, setStatus, snackBar, status, t]);

  return {
    contactSheets,
    loadingContactSheets,
  };
};

function convertToKpkContactSheet(
  currentLocale: string,
  kpkApiContactSheets: KpkApiContactSheet[] | undefined,
): KpkContactSheet[] {
  if (!Array.isArray(kpkApiContactSheets) && kpkApiContactSheets !== undefined) {
    kpkApiContactSheets = [kpkApiContactSheets];
  }
  const kpkContactSheets: KpkContactSheet[] = [];
  kpkApiContactSheets?.forEach((cs) => {
    const title =
      cs.title.find((t) => t.lang?.toLowerCase() === currentLocale)?.value ||
      cs.title.find((t) => t.lang?.toLowerCase() !== currentLocale)?.value;
    if (title !== undefined) {
      kpkContactSheets.push({
        id: cs.id,
        title: title,
        description: cs.description?.find((t) => t.lang?.toLowerCase() === currentLocale)?.value,
        orientation: cs.orientation,
        orientationAlterable: cs.orientationAlterable,
      });
    }
  });
  return kpkContactSheets;
}
